<template>
	<div class="report_section_container">
		<div class="detail_tab_container">
			<ConnectedSquareTab :items="detailTabItems" @update:detail-tab="onChangePeriod" />
		</div>
		<section class="section_finance">
			<h2 class="report_title">투자지표</h2>
			<div v-if="getInvestIndicatorData && currentData && Object.values(currentData)[0]" class="table_container_wrap">
				<div class="table_container">
					<table>
						<thead>
							<tr>
								<th></th>
								<th v-for="(item, idx) in Object.keys(currentData)" :key="idx">
									{{ item }}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in Object.keys(Object.values(currentData)[0])" :key="item">
								<td class="property">
									<p>{{ getTitle(replaceUnderscoreWithSpace(item)) }}</p>
									<p>{{ getTitleSub(replaceUnderscoreWithSpace(item)) }}</p>
								</td>
								<td v-for="(data, year) in currentData" :key="year" class="indicator_data">
									{{ numberFormatWithDecimal(data[item as keyof typeof data]) }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="report_guide">{{ FINANCE_INDICATOR }}</p>
			</div>
			<div v-else class="emptyData_alert">데이터 없음</div>
		</section>
	</div>
</template>

<script setup lang="ts">
// core
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

// api
import { useGetInvestIndicatorApi_JPN } from '@/api/report';
// component
import ConnectedSquareTab from '@/components/atom/tabBar/ConnectedSquareTab.vue';
// constant
import { FINANCE_INDICATOR } from '@/constants/title';
// util
import { replaceUnderscoreWithSpace } from '@/utils/function/format';
import { numberFormatWithDecimal } from '@/utils/function/format';

// -----variable-----
const route = useRoute();
const detailTabItems = ['연간', '분기'];
const selectedTab = ref();
const currentData = computed(() => {
	if (!selectedTab.value || selectedTab.value === '연간') {
		return getInvestIndicatorData.value?.invest_indicator.연간;
	} else {
		return getInvestIndicatorData.value?.invest_indicator.분기;
	}
});

// -----api-----
const { data: getInvestIndicatorData } = useGetInvestIndicatorApi_JPN(
	computed(() => {
		return { event_pk: String(route.params.eventPk) };
	})
);

// -----function-----
// 연간/분기 변경 시 실행 함수
const onChangePeriod = (tab: string) => {
	selectedTab.value = tab;
};

// 제목 추출 함수
interface TitleMapping {
	title: string;
	subtitle: string;
}
const TITLE_MAPPINGS: Record<string, TitleMapping> = {
	'주당 순이익(EPS)': { title: '주당 순이익', subtitle: 'EPS' },
	'주당 순자산(BPS)': { title: '주당 순자산', subtitle: 'BPS' },
	'주가수익비율(PER)': { title: '주가수익비율', subtitle: 'PER' },
	'주가순자산비율(PBR)': { title: '주가순자산비율', subtitle: 'PBR' },
	'자기자본 이익률(ROE)': { title: '자기자본 이익률', subtitle: 'ROE' },
	'총자산 순이익률(ROA)': { title: '총자산 순이익률', subtitle: 'ROA' },
	'투하자본 이익률(ROIC)': { title: '투하자본 이익률', subtitle: 'ROIC' },
	'EV/EBITDA': { title: 'EV/EBITDA', subtitle: '' },
	'배당성향(%)': { title: '배당성향', subtitle: '(%)' },
	'연간 배당률(%)': { title: '배당수익률', subtitle: '(%)' },
};
const getTitle = (key: string): string => {
	return TITLE_MAPPINGS[key]?.title ?? key;
};
const getTitleSub = (key: string): string => {
	return TITLE_MAPPINGS[key]?.subtitle ?? '';
};
</script>

<style scoped lang="scss">
.detail_tab_container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	select {
		background-color: var(--color-bg-mid);
		option {
			border: none;
		}
	}
}
.report_section_container {
	display: flex;
	flex-direction: column;
	gap: 4.8rem;
	.section_finance {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		.report_title {
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.387rem;
		}
		.table_container_wrap {
			display: flex;
			flex-direction: column;
			gap: 1.6rem;
		}

		.table_container {
			overflow-x: auto;
			table-layout: auto;
			padding-bottom: 1rem;
			table {
				border-collapse: collapse;
				width: 100%;
				th,
				td {
					text-align: center;
					padding: 1rem;
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: 1.551rem;
					border: 1px solid var(--color-base-mid-dark);
				}
				.indicator_data {
					text-align: center;
					vertical-align: middle;
				}
				.property {
					background: var(--color-bg-mid, #212c3f);
					width: 1%;
				}
				th {
					text-align: center;
					font-weight: var(--font-w-semi-bold);
					background: var(--color-bg-mid, #212c3f);
				}
				td:first-child {
					text-align: left;
					white-space: nowrap;
					font-weight: var(--font-w-semi-bold);
				}
			}
		}

		.report_guide {
			width: 100%;
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-light);
			line-height: 1.2;
			text-align: right;
			color: var(--color-base-light);
			@media screen and (max-width: 768px) {
				text-align: left;
			}
		}
	}
}
::-webkit-scrollbar {
	width: 17.6rem;
	height: 0.4rem;
}
::-webkit-scrollbar-track {
	background: var(--color-bg);
}
::-webkit-scrollbar-thumb {
	background: var(--color-base-mid);
}
::-webkit-scrollbar-thumb:hover {
	background: #bbb;
}
.emptyData_alert {
	padding: 3.2rem 0rem;
	font-size: var(--font-s-regular);
	font-weight: var(--font-w-light);
	line-height: 1.2;
	text-align: center;
}
</style>
